import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";

import {
    FormDateRangePicker,
    FormDropDownList,
    FormAutoComplete,
    FormCheckbox,
} from "../../forms/form-components";

export const AdministratorTriggeredVenueEventAlerts = () => {
    const dispatch = useDispatch();

    const formReference = useRef(null);

    const defaultTicketingWebsite = {
        ticketingWebsiteId: "",
        name: "All Websites",
    };
    const defaultVenueEventStatusType = {
        venueEventStatusTypeId: "",
        name: "All Statuses",
    };
    const defaultVenueEventAlertType = {
        venueEventAlertTypeId: "",
        name: "All Types",
    };
    const alertSuccessfulOptions = [
        {
            value: "",
            name: "All Values",
        },
        {
            value: "true",
            name: "True",
        },
        {
            value: "false",
            name: "False",
        }
    ];

    const [ticketingWebsites, setTicketingWebsites] = useState([]);
    const [venueEventStatusTypes, setVenueEventStatusTypes] = useState([]);
    const [venueEventAlertTypes, setVenueEventAlertTypes] = useState([]);

    const [venueEventAlerts, setVenueEventAlerts] = useState(undefined);
    const [venueEventAlertCount, setVenueEventAlertCount] = useState(0);
    const [skipCount, setSkipCount] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(1);
    const [venues, setVenues] = useState([]);
    const [venueName, setVenueName] = useState(undefined);
    const [areVenuesLoading, setAreVenuesLoading] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [dataLoadingError, setDataLoadingError] = useState(undefined);
    const [searchCriteria, setSearchCriteria] = useState(undefined);

    const autocompleteTimeout = useRef();
    const autocompleteTimeoutDelay = 500; //Milliseconds

    const [showDialog, setShowDialog] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogContent, setDialogContent] = useState("");

    const getTicketingWebsites = () => {
        const dataItem = {
            onlyActive: true,
        }

        fetch(`/api/general/ticketingwebsites`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                const allTicketingWebsites = [defaultTicketingWebsite].concat(result);
                setTicketingWebsites(allTicketingWebsites);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const getVenueEventStatusTypes = () => {
        fetch(`/api/general/venueeventstatustypes`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                const allVenueEventStatusTypes = [defaultVenueEventStatusType].concat(result);
                setVenueEventStatusTypes(allVenueEventStatusTypes);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const getVenueEventAlertTypes = () => {
        fetch(`/api/general/venueeventalerttypes`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                const allVenueEventAlertTypes = [defaultVenueEventAlertType].concat(result);
                setVenueEventAlertTypes(allVenueEventAlertTypes);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const venueChanged = (event) => {
        const value = event.value;

        clearTimeout(autocompleteTimeout.current);

        if (!value || value.length < 2) {
            setVenues([]);
            setAreVenuesLoading(false);
            setVenueName(null);
            return;
        }
        
        autocompleteTimeout.current = setTimeout(() => {
            const dataItem = {
                name: value,
                onlyActive: true,
            };

            fetch(`/api/administrator/venues`,
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        "X-XSRF-TOKEN": getCSRFToken(),
                    },
                    body: JSON.stringify(dataItem),
                })
                .then(handleResponse)
                .then(result => {
                    setVenues(result.venues);  
                    setVenueName(value);
                })
                .catch(error => {
                    addNotificationWithTimeout(dispatch, "error", error.message);
                })
                .finally(() => {
                    setAreVenuesLoading(false);
                });
        }, autocompleteTimeoutDelay);

        setAreVenuesLoading(true);
    };

    const getVenueEventAlerts = (dataItem, newPageNumber) => {
        setIsDataLoading(true);

        if (!dataItem) {
            dataItem = searchCriteria;
        }
        
        dataItem.pageNumber = newPageNumber;
    
        fetch(`/api/administrator/report/triggeredvenueeventalerts`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setIsDataLoading(false);                
                setItemsPerPage(result.venueEventAlertsPerPage);
                setSkipCount(result.venueEventAlertsPerPage * (result.pageNumber - 1));
                setVenueEventAlertCount(result.totalVenueEventAlertCount);                
                setVenueEventAlerts(result.venueEventAlerts);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsDataLoading(false);
                setDataLoadingError(error.message);
            });
    };

    const getData = (dataItem) => {
        if (dataItem.groupByAlert === undefined) {
            dataItem.groupByAlert = true;
        }

        if (dataItem.isSuccessful === undefined) {
            dataItem.isAlertSuccessful = true;
        }
        else if (dataItem.isSuccessful.value !== "") {
            dataItem.isAlertSuccessful = dataItem.isSuccessful.value === "true";
        }
        else {
            dataItem.isAlertSuccessful = undefined;
        }

        if (dataItem.venueEventAlertType !== undefined
            && dataItem.venueEventAlertType.value !== "") {
            dataItem.venueEventAlertTypeId = parseInt(dataItem.venueEventAlertType.venueEventAlertTypeId);
        }
        else {
            dataItem.venueEventAlertTypeId = undefined;
        }

        if (dataItem.ticketingWebsite !== undefined
            && dataItem.ticketingWebsite.value !== "") {
            dataItem.ticketingWebsiteId = parseInt(dataItem.ticketingWebsite.ticketingWebsiteId);
        }
        else {
            dataItem.ticketingWebsiteId = undefined;
        }

        if (dataItem.venueEventStatusType !== undefined
            && dataItem.venueEventStatusType.value !== "") {
            dataItem.venueEventStatusTypeId = parseInt(dataItem.venueEventStatusType.venueEventStatusTypeId);
        }
        else {
            dataItem.venueEventStatusTypeId = undefined;
        }

        dataItem.venueKey = undefined;
        if (venues.length > 0) {
            const venue = venues.find(element => element.currentName === venueName);

            if (venue) {
                dataItem.venueKey = venue.venueKey;
            }
        }

        dataItem.startingAlertDate = undefined;
        dataItem.endingAlertDate = undefined;
        if (dataItem.alertDates) {
            if (dataItem.alertDates.start) {
                dataItem.startingAlertDate = dataItem.alertDates.start;
            }

            if (dataItem.alertDates.end) {
                dataItem.endingAlertDate = dataItem.alertDates.end;
            }
        }

        dataItem.startingEventDate = undefined;
        dataItem.endingEventDate = undefined;
        if (dataItem.eventDates) {
            if (dataItem.eventDates.start) {
                dataItem.startingEventDate = dataItem.eventDates.start;
            }

            if (dataItem.eventDates.end) {
                dataItem.endingEventDate = dataItem.eventDates.end;
            }
        }
        
        setSearchCriteria(dataItem);
        getVenueEventAlerts(dataItem);
    }

    const pageChange = (event) => {
        const newPageNumber = (event.page.skip / event.page.take) + 1;
        getVenueEventAlerts(null, newPageNumber);
    };

    const ViewDialogCell = (props) => {  
        const content = props.dataItem[props.field];
        const title = props.field[0].toUpperCase() + props.field.slice(1);        

        return (
            <td className={"text-nowrap"}>
                {content ? <Button fillMode="link" type={"button"} onClick={() => viewDialog(title, content)}>View Notes</Button> : ""}
            </td>
        );
    };

    const ViewUrlCell = (props) => {  
        const url = props.dataItem[props.field];     

        return (
            <td className={"text-nowrap"}>
                {url ? <Link to={{ pathname: url }} target={"_blank"} rel={"noopener noreferrer"}>View Website</Link> : ""}
            </td>
        );
    };

    const viewDialog = (title, content) => {
        setDialogTitle(title);
        setDialogContent(content);
        setShowDialog(true);
    }

    useEffect(() => {
        document.title = "Triggered Event Alerts";

        getTicketingWebsites();
        getVenueEventStatusTypes();
        getVenueEventAlertTypes();
    }, []);

    return (
        <div className="administratorReport">
            <Form
                onSubmit={getData}
                ignoreModified={true}
                ref={formReference}
                render={(formRenderProps) => (
                    <FormElement>
                        <fieldset className={"k-form-fieldset container"}>
                            <div className="row">      
                                <div className="col-12">
                                    <Field
                                        id={"groupByAlert"}
                                        name={"groupByAlert"}
                                        label={"Group by Alert"}
                                        component={FormCheckbox}
                                        defaultValue={true}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <Field
                                        id={"isSuccessful"}
                                        name={"isSuccessful"}
                                        label={"Alert successful"}
                                        component={FormDropDownList}
                                        data={alertSuccessfulOptions}
                                        textField="name"
                                        dataItemKey="value"
                                        defaultValue={alertSuccessfulOptions.find(element => element.value === "true")}
                                    /> 
                                </div> 
                                <div className="col-md-4">
                                    <Field
                                        id={"venueEventAlertType"}
                                        name={"venueEventAlertType"}
                                        label={"Alert type"}
                                        component={FormDropDownList}
                                        data={venueEventAlertTypes}
                                        textField="name"
                                        dataItemKey="venueEventAlertTypeId"
                                        defaultValue={venueEventAlertTypes.find(element => element.venueEventAlertTypeId === "")}
                                    /> 
                                </div>
                                <div className="col-md-4">
                                    <Field
                                        id={"ticketingWebsite"}
                                        name={"ticketingWebsite"}
                                        label={"Ticketing website"}
                                        component={FormDropDownList}
                                        data={ticketingWebsites}
                                        textField="name"
                                        dataItemKey="ticketingWebsiteId"
                                        defaultValue={ticketingWebsites.find(element => element.ticketingWebsiteId === "")}
                                    /> 
                                </div>
                                <div className="col-md-4">
                                    <Field
                                        id={"venueEventStatusType"}
                                        name={"venueEventStatusType"}
                                        label={"Event status"}
                                        component={FormDropDownList}
                                        data={venueEventStatusTypes}
                                        textField="name"
                                        dataItemKey="venueEventStatusTypeId"
                                        defaultValue={venueEventStatusTypes.find(element => element.venueEventStatusTypeId === "")}
                                    /> 
                                </div>
                                <div className="col-md-8">
                                    <Field
                                        id={"venueName"}
                                        name={"venueName"}
                                        label={"Venue"}
                                        component={FormAutoComplete}
                                        data={venues}        
                                        placeholder="e.g. Roseland"
                                        textField="currentName"
                                        onChange={venueChanged}
                                        loading={areVenuesLoading}
                                    />
                                </div>
                                <div className="col-md-6 col-lg-5">
                                    <Field
                                        id={"alertDates"}
                                        name={"alertDates"}
                                        component={FormDateRangePicker}
                                        format={"MMM dd, yyyy"}
                                        startDateInputSettings={{
                                            label: "Starting alert date"
                                        }}
                                        endDateInputSettings={{
                                            label: "Ending alert date"
                                        }}                                        
                                        calendarSettings={{
                                            views: 1
                                        }}
                                    />
                                </div>  
                                <div className="col-md-6 col-lg-5">
                                    <Field
                                        id={"eventDates"}
                                        name={"eventDates"}
                                        component={FormDateRangePicker}
                                        format={"MMM dd, yyyy"}
                                        startDateInputSettings={{
                                            label: "Starting event date"
                                        }}
                                        endDateInputSettings={{
                                            label: "Ending event date"
                                        }}                                        
                                        calendarSettings={{
                                            views: 1
                                        }}
                                    />
                                </div> 
                                <div className="col-lg-2">
                                    <div className="k-form-field">
                                        <label className="k-label">&nbsp;</label>
                                        <Button
                                            type={"submit"}
                                            themeColor={"primary"}
                                            icon="search"                                            
                                        >
                                            Search
                                        </Button>
                                    </div>
                                </div>
                            </div>                        
                        </fieldset>
                    </FormElement>
                )}
            />

            { isDataLoading && <LoadingPanel /> }

            { dataLoadingError && 
                <div>
                    {dataLoadingError}
                </div>
            }

            { venueEventAlerts &&
                <Grid
                    data={venueEventAlerts}
                    resizable={true}
                    skip={skipCount}
                    take={itemsPerPage}
                    total={venueEventAlertCount}
                    pageable={true}
                    onPageChange={pageChange}
                    scrollable={"none"}
                >        
                    <Column field="ticketingUrl" title={"Website"} cell={ViewUrlCell} />
                    <Column field="dateCreated" title={"Created"} />
                    <Column field="eventName" title={"Event"} />
                    <Column field="venueName" title={"Venue"} />
                    <Column field="eventDate" title={"Event Date"} />
                    <Column field="venueEventStatusTypeName" title={"Event Status"} />
                    <Column field="isAlertSuccessful" title={"Alert Successful"} />
                    <Column field="sectionName" title={"Section"} />
                    <Column field="venueEventAlertTypeName" title={"Type"} />    
                    <Column field="ticketCount" title={"Count"} />
                    <Column field="notes" title={"Notes"} cell={ViewDialogCell} />                    
                </Grid>
            }

            {showDialog === false ? <></> :
                <Dialog
                    title={dialogTitle}
                    onClose={() => setShowDialog(false)}
                    width={"90vw"}
                >
                    {dialogContent}
                </Dialog>
            }
        </div>
    );
}